"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { getFlatpickrLocale } from "@/utils/flatpickrLocale";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useFlatpickrField } from "@/hooks/useFlatpickrField";

interface BirthdateFieldProps {
  birthdate: Date | undefined;
  error: string;
  disabled?: boolean;
  onBirthdateChange: (date: Date | undefined) => void;
}

const BirthdateField: React.FC<BirthdateFieldProps> = ({
  birthdate,
  error,
  disabled = false,
  onBirthdateChange,
}) => {
  const { t, i18n } = useTranslation();

  const flatpickrOptions = React.useMemo(() => {
    const today = new Date();
    const minDate = new Date(1900, 0, 1);
    return {
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d.m.Y",
      maxDate: today,
      minDate,
      locale: getFlatpickrLocale(i18n.language),
      disableMobile: true,
      allowInput: true,
      clickOpens: false,
      // flatpickr clones the input into a visible `altInput` without copying these attributes.
      onReady: (_dates: Date[], _dateStr: string, instance: { altInput?: HTMLInputElement }) => {
        instance.altInput?.setAttribute("name", "birthdate");
        instance.altInput?.setAttribute("autocomplete", "bday");
      },
    };
  }, [i18n.language]);

  const handleFlatpickrChange = (dates: Date[]) => onBirthdateChange(dates[0] ?? undefined);

  const { inputRef, instanceRef } = useFlatpickrField(flatpickrOptions, birthdate, handleFlatpickrChange);

  return (
    <div className="space-y-2 field-row">
      <label htmlFor="birthdate" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        {t("contactDetails.birthdate")}
      </label>
      <div className="relative">
        <Input
          id="birthdate"
          name="birthdate"
          autoComplete="bday"
          // See the matching comment in BirthDateInput.tsx: React unconditionally re-asserts an
          // `<input>`'s `type` on every re-render of its fiber, which kept resetting Flatpickr's
          // `type="hidden"` mutation back to "text" — declaring it here too makes that forced
          // reassertion agree with Flatpickr instead of fighting it.
          type="hidden"
          placeholder={t("common.datePlaceholder")}
          disabled={disabled}
          className={cn("pr-10", error && "border-destructive focus-visible:ring-destructive")}
          ref={inputRef}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
          onClick={() => instanceRef.current?.open()}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="sr-only">
            {t("common.openCalendar")}
          </span>
        </Button>
      </div>
      <p className="text-xs text-muted-primary">
        {t("pclosing-pet-parent-birthdate-explanation")}
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

// `onBirthdateChange` is excluded: ContactDetailsForm passes a fresh inline closure on every
// render that always delegates to the same stable useState setters, so treating it as "changed"
// would only force useFlatpickrField's value-sync effect to rerun redundantly (harmless, since
// it no-ops when the timestamp hasn't changed, but pointless).
// `birthdate` is compared by timestamp, not reference, for the same reason: Flatpickr's own
// `allowInput` handling can settle on an equal-but-new Date instance without the calendar day
// actually changing.
const arePropsEqual = (prev: BirthdateFieldProps, next: BirthdateFieldProps) =>
  prev.birthdate?.getTime() === next.birthdate?.getTime() &&
  prev.error === next.error &&
  prev.disabled === next.disabled;

export default React.memo(BirthdateField, arePropsEqual);
